'use client';
import HistoryIcon from '@mui/icons-material/History';
import { Box, Typography } from '@mui/material';

import { PreviousMarketsSkeleton } from '@/components/skeletons';
import { useT } from '@/hooks';
import { usePreviousMarkets } from '@/services/query';
import { EventResponse } from '@/types';
import { formatDate } from '@/utils';
import { GameListByDate } from './GameListByDate';

interface PreviousMarketsViewProps {
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  columnVisibility: { hasMoneyline: boolean; hasSpread: boolean; hasTotal: boolean };
  activeOutcomeId?: string;
  series_id?: string;
  searchQuery?: string;
}

export const PreviousMarketsView = ({
  onTeamClick,
  onPropTradeClick,
  columnVisibility,
  activeOutcomeId,
  series_id,
  searchQuery,
}: PreviousMarketsViewProps) => {
  const { t } = useT();
  const { data: previousMarkets, isLoading } = usePreviousMarkets({
    series_id,
    search: searchQuery,
  });

  const filteredMarkets = (previousMarkets || []).filter(
    (event) => !!event.homeTeam && !!event.awayTeam && !!event.seriesSlug,
  );

  const gamesByDate = filteredMarkets.reduce(
    (acc, event) => {
      // Prefer gameDate > eventDate > startDate > endDate
      const rawDate = event.gameDate || event.eventDate || event.startDate || event.endDate;
      const dateKey = rawDate
        ? formatDate(rawDate, { weekday: 'short', month: 'long', day: 'numeric' })
        : '-';

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(event);
      return acc;
    },
    {} as Record<string, EventResponse[]>,
  );

  const renderGameGroup = ([date, gamesList]: [string, EventResponse[]]) => {
    return (
      <Box key={date} sx={{ width: '100%', mb: 2 }}>
        <GameListByDate
          date={date}
          games={gamesList || []}
          sportSlug="previous"
          onTeamClick={onTeamClick}
          onPropTradeClick={onPropTradeClick}
          columnVisibility={columnVisibility}
          activeOutcomeId={activeOutcomeId}
          isPreviousMarket={true}
        />
      </Box>
    );
  };

  const marketsList = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }}>
      {Object.entries(gamesByDate).map(renderGameGroup)}
    </Box>
  );

  return (
    <Box
      sx={{
        mt: 1,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
        <Typography variant="subtitle1" fontWeight={600}>
          {t('sports.previousMarkets', { defaultValue: 'Previous Markets' })}
        </Typography>
      </Box>

      {isLoading ? (
        <PreviousMarketsSkeleton />
      ) : filteredMarkets.length > 0 ? (
        marketsList
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {t('sports.noPreviousMarkets', {
            defaultValue: 'No previous markets found for this category.',
          })}
        </Typography>
      )}
    </Box>
  );
};
